import {
    announceTemplateVersions,
    hasLocalWork,
    mergeInspection,
    removeInspection,
} from './bootstrap';
import { followAnswer } from './follow-answer';
import { settledGeneration } from './push';
import { sameRecord } from './same-record';
import { SYNC_META_KEYS, type AnswerRecord } from './records';
import {
    answerRecordFrom,
    entityKeys,
    findingPhotoRecordFrom,
    findingRecordFrom,
    inspectionRecordFrom,
    multiCountRecordFrom,
    orderRecordFrom,
    photoRecordFrom,
} from './sync-records';
import type { TenantDatabase } from './tenant-db';
import {
    wireAnswerSchema,
    wireFindingSchema,
    wireInspectionSchema,
    wireMultiCountSchema,
    wireOrderSchema,
    wirePhotoSchema,
    wireTemplateVersionSchema,
    type PullChange,
    type PullData,
} from './wire';

/**
 * Eén pagina van `GET /sync/pull` toepassen. Net als bij de bootstrap wint wat
 * lokaal nog in de outbox staat; de push beslist daar later over.
 *
 * Een wijziging die niet te plaatsen is (onbekende entiteit, of een antwoord
 * van een opname die niet op dit apparaat staat) wordt overgeslagen: de pull
 * mag nooit vastlopen op één regel.
 */
export interface PullSummary {
    applied: number;
    skipped: number;
    /** Er is een nieuwe templateversie aangekondigd; pakketten ophalen. */
    templateVersionsChanged: boolean;
    /**
     * Terwijl deze pagina onderweg was, verliet een bewerking de outbox (zie
     * `SYNC_META_KEYS.settled`). Er is niets toegepast en de cursor staat nog: haal
     * de pagina opnieuw op.
     */
    stale: boolean;
}

/**
 * @param settled de `settledGeneration()` van vlak vóór het verzoek; weglaten slaat de controle over
 */
export async function applyPull(
    db: TenantDatabase,
    page: PullData,
    now: number = Date.now(),
    settled?: number,
): Promise<PullSummary> {
    const summary: PullSummary = {
        applied: 0,
        skipped: 0,
        templateVersionsChanged: false,
        stale: false,
    };

    await db.transaction(
        'rw',
        [
            db.templateVersions,
            db.packageDocuments,
            db.orders,
            db.inspections,
            db.answers,
            db.multiCounts,
            db.findings,
            db.photos,
            db.outbox,
            db.syncMeta,
        ],
        async () => {
            if (settled !== undefined && (await settledGeneration(db)) !== settled) {
                summary.stale = true;

                return;
            }

            const pending = new Set((await db.outbox.toArray()).map((op) => op.entityKey));

            for (const change of page.changes) {
                const applied = await applyChange(db, change, pending, now);

                if (applied === 'template') {
                    summary.templateVersionsChanged = true;
                }

                if (applied) {
                    summary.applied += 1;
                } else {
                    summary.skipped += 1;
                }
            }

            await db.syncMeta.put({ key: SYNC_META_KEYS.cursor, value: page.cursor });
        },
    );

    return summary;
}

type Applied = boolean | 'template';

async function applyChange(
    db: TenantDatabase,
    change: PullChange,
    pending: ReadonlySet<string>,
    now: number,
): Promise<Applied> {
    const upsert = change.action === 'upsert';

    switch (change.entity) {
        case 'order': {
            if (!upsert) {
                if (!change.uuid) {
                    return false;
                }

                await db.orders.delete(change.uuid);

                return true;
            }

            const parsed = wireOrderSchema.safeParse(change.data);

            if (!parsed.success || pending.has(entityKeys.order(parsed.data.uuid))) {
                return false;
            }

            const order = orderRecordFrom(parsed.data);

            if (!sameRecord(await db.orders.get(order.uuid), order)) {
                await db.orders.put(order);
            }

            return true;
        }

        case 'inspection': {
            if (!upsert) {
                if (!change.uuid || (await hasLocalWork(db, change.uuid))) {
                    return false;
                }

                await removeInspection(db, change.uuid);

                return true;
            }

            const parsed = wireInspectionSchema.safeParse(change.data);

            if (!parsed.success) {
                return false;
            }

            const inspection = parsed.data;

            /*
             * Een `inspection`-upsert draagt de volledige bootstrapvorm: wat de
             * server niet meer heeft, is weg (behalve lokaal werk). Zonder de
             * lijsten is het alleen de kop, en blijven de kinderen staan.
             */
            if (carriesChildren(change.data)) {
                await mergeInspection(db, inspection, pending, now);

                return true;
            }

            if (!pending.has(entityKeys.inspection(inspection.uuid))) {
                const local = await db.inspections.get(inspection.uuid);
                const record = inspectionRecordFrom(inspection, local);

                if (!sameRecord(local, record)) {
                    await db.inspections.put(record);
                }
            }

            return true;
        }

        case 'inspection_answer': {
            if (!upsert) {
                const local = change.uuid ? await db.answers.get(change.uuid) : undefined;

                if (!local || pending.has(entityKeys.answer(local.inspectionUuid, local.slot))) {
                    return false;
                }

                await db.answers.delete(local.answerUuid);

                return true;
            }

            const parsed = wireAnswerSchema.safeParse(change.data);

            if (!parsed.success) {
                return false;
            }

            const inspectionUuid =
                parsed.data.inspection_uuid ??
                (await db.answers.get(parsed.data.answer_uuid))?.inspectionUuid;

            return inspectionUuid ? upsertAnswer(db, inspectionUuid, parsed.data, pending) : false;
        }

        case 'multi_count': {
            const parsed = wireMultiCountSchema.safeParse(change.data);

            if (!upsert || !parsed.success || !parsed.data.inspection_uuid) {
                return false;
            }

            return upsertMultiCount(db, parsed.data.inspection_uuid, parsed.data, pending);
        }

        case 'finding': {
            if (!upsert) {
                const local = change.uuid ? await db.findings.get(change.uuid) : undefined;

                if (!local || pending.has(entityKeys.finding(local.findingUuid))) {
                    return false;
                }

                await db.findings.delete(local.findingUuid);

                return true;
            }

            const parsed = wireFindingSchema.safeParse(change.data);
            const inspectionUuid = parsed.success
                ? (parsed.data.inspection_uuid ??
                  (await db.findings.get(parsed.data.finding_uuid))?.inspectionUuid)
                : undefined;

            if (
                !parsed.success ||
                !inspectionUuid ||
                pending.has(entityKeys.finding(parsed.data.finding_uuid))
            ) {
                return false;
            }

            const finding = findingRecordFrom(inspectionUuid, parsed.data);

            if (!sameRecord(await db.findings.get(finding.findingUuid), finding)) {
                await db.findings.put(finding);
            }

            return true;
        }

        case 'photo':
        case 'media': {
            if (!upsert) {
                /* Op `client_uuid`, zoals de upserts; een foto zonder die sleutel op zijn media-uuid. */
                const uuid = change.uuid;
                const local = uuid
                    ? ((await db.photos.get(uuid)) ??
                      (await db.photos.filter((photo) => photo.mediaUuid === uuid).first()))
                    : undefined;

                if (!local || local.state !== 'uploaded') {
                    return false;
                }

                await db.photos.delete(local.clientUuid);

                return true;
            }

            const parsed = wirePhotoSchema.safeParse(change.data);

            if (!parsed.success) {
                return false;
            }

            const answer = parsed.data.answer_uuid
                ? await db.answers.get(parsed.data.answer_uuid)
                : undefined;
            const finding = parsed.data.finding_uuid
                ? await db.findings.get(parsed.data.finding_uuid)
                : undefined;
            const inspectionUuid =
                parsed.data.inspection_uuid ?? answer?.inspectionUuid ?? finding?.inspectionUuid;

            return inspectionUuid ? upsertPhoto(db, inspectionUuid, parsed.data, now) : false;
        }

        case 'template_version': {
            const parsed = wireTemplateVersionSchema.safeParse(change.data);

            if (!upsert || !parsed.success) {
                return false;
            }

            await announceTemplateVersions(db, [parsed.data]);

            return 'template';
        }

        default:
            return false;
    }
}

async function upsertAnswer(
    db: TenantDatabase,
    inspectionUuid: string,
    answer: Parameters<typeof answerRecordFrom>[1],
    pending: ReadonlySet<string>,
): Promise<boolean> {
    const record: AnswerRecord = answerRecordFrom(inspectionUuid, answer);

    if (pending.has(entityKeys.answer(inspectionUuid, record.slot))) {
        return false;
    }

    await db.answers
        .where('[inspectionUuid+slot]')
        .equals([inspectionUuid, record.slot])
        .filter((local) => local.answerUuid !== record.answerUuid)
        .delete();

    const local = await db.answers.get(record.answerUuid);

    /* De eigen antwoorden van dit apparaat komen na elke push terug: niets te schrijven. */
    if (!sameRecord(local, record)) {
        await followAnswer(db, local, record);
        await db.answers.put(record);
    }

    return true;
}

async function upsertMultiCount(
    db: TenantDatabase,
    inspectionUuid: string,
    count: Parameters<typeof multiCountRecordFrom>[1],
    pending: ReadonlySet<string>,
): Promise<boolean> {
    const record = multiCountRecordFrom(inspectionUuid, count);

    if (pending.has(entityKeys.multiCount(inspectionUuid, record.slot))) {
        return false;
    }

    if (!sameRecord(await db.multiCounts.get([inspectionUuid, record.slot]), record)) {
        await db.multiCounts.put(record);
    }

    return true;
}

async function upsertPhoto(
    db: TenantDatabase,
    inspectionUuid: string,
    photo: Parameters<typeof photoRecordFrom>[1],
    now: number,
): Promise<boolean> {
    const local = await db.photos.get(photo.client_uuid);

    const answer = photo.answer_uuid ? await db.answers.get(photo.answer_uuid) : undefined;
    const record = photo.finding_uuid
        ? findingPhotoRecordFrom(
              inspectionUuid,
              { ...photo, finding_uuid: photo.finding_uuid },
              local,
              now,
          )
        : answer
          ? photoRecordFrom(inspectionUuid, photo, answer, local, now)
          : undefined;

    if (!record) {
        return false;
    }

    if (!sameRecord(local, record)) {
        await db.photos.put(record);
    }

    return true;
}

function carriesChildren(data: unknown): boolean {
    return (
        typeof data === 'object' &&
        data !== null &&
        'answers' in data &&
        Array.isArray(data.answers)
    );
}
